import { useState, useEffect } from 'react'
import { X, Save, Upload } from 'lucide-react'
import { useToast } from '../../contexts/ToastContext'
import { supabase } from '../../lib/supabase'
import { convertMarkdownToHtml } from './htmlUtils'
import RichTextEditor from './RichTextEditor'
import FileUploader from './FileUploader'

interface Lesson {
  id?: string
  module_id: string
  title: string
  content: string | null
  video_url: string | null
  resources: any
  order_index: number
  duration_minutes: number | null
  is_published: boolean
}

interface LessonModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (lesson: Lesson) => void
  courseId: string
  moduleId: string
  lesson?: Lesson | null
  maxOrderIndex: number
}

export default function LessonModal({
  isOpen,
  onClose,
  onSave,
  courseId,
  moduleId,
  lesson,
  maxOrderIndex
}: LessonModalProps) {
  const [formData, setFormData] = useState<Lesson>({
    module_id: moduleId,
    title: '',
    content: '',
    video_url: '',
    resources: null,
    order_index: maxOrderIndex + 1,
    duration_minutes: null,
    is_published: false
  })
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'content' | 'video' | 'resources'>('content')
  const { showToast } = useToast()

  useEffect(() => {
    if (lesson) {
      // Convertir contenido existente para mostrarlo correctamente en el editor
      let processedContent = lesson.content || ''
      
      // Si el contenido no está ya en formato HTML, convertirlo
      if (processedContent && !processedContent.includes('<p>') && !processedContent.includes('<h1>') && !processedContent.includes('<h2>')) {
        processedContent = convertMarkdownToHtml(processedContent)
      }
      
      setFormData({
        ...lesson,
        content: processedContent
      })
    } else {
      setFormData({
        module_id: moduleId,
        title: '',
        content: '',
        video_url: '',
        resources: null,
        order_index: maxOrderIndex + 1,
        duration_minutes: null,
        is_published: false
      })
    }
  }, [lesson, moduleId, maxOrderIndex])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.title.trim()) {
      showToast('error', 'El título es obligatorio')
      return
    }

    setSaving(true)

    try {
      // Convertir contenido a HTML antes de guardar
      const processedContent = formData.content ? convertMarkdownToHtml(formData.content) : null

      if (lesson?.id) {
        // Actualizar lección existente
        const { data, error } = await supabase
          .from('course_lessons')
          .update({
            title: formData.title,
            content: processedContent,
            video_url: formData.video_url || null,
            resources: formData.resources,
            duration_minutes: formData.duration_minutes || null,
            is_published: formData.is_published
          })
          .eq('id', lesson.id)
          .select()
          .single()

        if (error) throw error
        showToast('success', 'Lección actualizada exitosamente')
        onSave({...data, content: processedContent})
      } else {
        // Crear nueva lección
        const { data, error } = await supabase
          .from('course_lessons')
          .insert([{
            module_id: moduleId,
            title: formData.title,
            content: processedContent,
            video_url: formData.video_url || null,
            resources: formData.resources,
            order_index: formData.order_index,
            duration_minutes: formData.duration_minutes || null,
            is_published: formData.is_published
          }])
          .select()
          .single()

        if (error) throw error
        showToast('success', 'Lección creada exitosamente')
        onSave({...data, content: processedContent})
      }

      onClose()
    } catch (error: any) {
      console.error('Error saving lesson:', error)
      const errorMessage = 'Error al guardar lección: ' + error.message
      showToast('error', errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleFileUploaded = (url: string) => {
    if (activeTab === 'video') {
      setFormData({ ...formData, video_url: url })
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-education px-6 py-4 flex items-center justify-between sticky top-0 z-10">
            <h3 className="text-lg font-semibold text-white">
              {lesson ? 'Editar Lección' : 'Crear Nueva Lección'}
            </h3>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSave} className="p-6 space-y-6">
            {/* Información básica */}
            <div className="grid grid-cols-2 gap-4">
              {/* Título */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                  placeholder="Ej: Introducción a la Economía Circular"
                />
              </div>

              {/* Duración */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duración (minutos)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.duration_minutes || ''}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                  placeholder="Ej: 15"
                />
              </div>

              {/* Estado de Publicación */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="lesson_is_published"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="h-4 w-4 text-education focus:ring-education border-gray-300 rounded"
                />
                <label htmlFor="lesson_is_published" className="ml-2 block text-sm text-gray-700">
                  Publicar lección
                </label>
              </div>
            </div>

            {/* Tabs para contenido */}
            <div>
              <div className="flex border-b border-gray-200 mb-4">
                <button
                  type="button"
                  onClick={() => setActiveTab('content')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${
                    activeTab === 'content'
                      ? 'border-education text-education'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Contenido de Texto
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('video')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${
                    activeTab === 'video'
                      ? 'border-education text-education'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Video
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('resources')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${
                    activeTab === 'resources'
                      ? 'border-education text-education'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Recursos
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === 'content' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contenido de la lección
                  </label>
                  <RichTextEditor
                    value={formData.content || ''}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    placeholder="Escribe el contenido de la lección aquí..."
                    className="min-h-[400px]"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Usa el editor para formatear texto, agregar listas, links, imágenes y más.
                  </p>
                </div>
              )}

              {activeTab === 'video' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL del Video
                    </label>
                    <input
                      type="url"
                      value={formData.video_url || ''}
                      onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                      placeholder="https://youtube.com/watch?v=... o URL de video subido"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Puedes usar YouTube, Vimeo o subir tu propio video
                    </p>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      O sube un video
                    </label>
                    <FileUploader
                      courseId={courseId}
                      onFileUploaded={handleFileUploaded}
                      acceptedTypes="video/*"
                      maxSizeMB={100}
                    />
                  </div>

                  {formData.video_url && (
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <p className="text-xs text-gray-600 font-medium mb-1">Vista previa:</p>
                      <p className="text-sm text-gray-800 truncate">{formData.video_url}</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'resources' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Los recursos adicionales permiten a los estudiantes descargar archivos complementarios.
                  </p>
                  <FileUploader
                    courseId={courseId}
                    onFileUploaded={(url) => {
                      const currentResources = formData.resources || []
                      const newResources = Array.isArray(currentResources) ? currentResources : []
                      setFormData({
                        ...formData,
                        resources: [...newResources, { url, name: 'Recurso adicional' }]
                      })
                    }}
                    acceptedTypes="application/pdf,image/*"
                    maxSizeMB={50}
                  />

                  {formData.resources && Array.isArray(formData.resources) && formData.resources.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Recursos añadidos:</p>
                      {formData.resources.map((resource: any, index: number) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                          <span className="text-sm text-gray-700 truncate">{resource.name || resource.url}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const newResources = formData.resources.filter((_: any, i: number) => i !== index)
                              setFormData({ ...formData, resources: newResources })
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-education text-white rounded-lg hover:bg-education/90 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Guardando...' : 'Guardar Lección'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
